import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import profileService, { PrivacyPreferences } from '../../services/profile';
import './PrivacySettings.css';

/**
 * Componente de página para configurações de privacidade do usuário
 * Permite gerenciar preferências de notificações e privacidade
 */
const PrivacySettings: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Preferências de privacidade
  const [preferences, setPreferences] = useState<PrivacyPreferences>({
    notificacoes_email: true,
    notificacoes_sistema: true,
    perfil_visivel: true,
    compartilhar_dados: false
  });

  /**
   * Carrega as preferências ao montar o componente
   */
  useEffect(() => {
    loadPreferences();
  }, []);

  /**
   * Carrega as preferências de privacidade do usuário
   */
  const loadPreferences = async () => {
    try {
      setLoading(true);
      const data = await profileService.getPrivacyPreferences();
      setPreferences(data);
    } catch (error: any) {
      console.error('Erro ao carregar preferências:', error);
      // Usa valores padrão em caso de erro
    } finally {
      setLoading(false);
    }
  };

  /**
   * Atualiza uma preferência específica
   */
  const handleTogglePreference = (key: keyof PrivacyPreferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  /**
   * Salva as preferências de privacidade
   */
  const handleSavePreferences = async () => {
    try {
      setSaving(true);
      await profileService.updatePrivacyPreferences(preferences);
      alert('Preferências de privacidade atualizadas com sucesso!');
    } catch (error: any) {
      console.error('Erro ao salvar preferências:', error);
      alert(error.response?.data?.message || 'Erro ao salvar preferências');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="privacy-settings-container">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="privacy-settings-container">
      <div className="privacy-settings-header">
        <div className="header-content">
          <h1>
            <i className="fas fa-shield-alt"></i>
            Privacidade e Segurança
          </h1>
          <p>Gerencie suas preferências de privacidade e notificações</p>
        </div>
      </div>

      <div className="privacy-settings-content">
        {/* Seção de Notificações */}
        <div className="settings-section">
          <div className="section-header">
            <h2>
              <i className="fas fa-bell"></i>
              Notificações
            </h2>
            <p>Escolha como você deseja receber notificações do sistema</p>
          </div>

          <div className="settings-list">
            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-title">
                  <i className="fas fa-envelope"></i>
                  Notificações por E-mail
                </div>
                <p className="setting-description">
                  Receba notificações importantes por e-mail sobre atualizações do sistema,
                  lembretes e alertas.
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences.notificacoes_email}
                  onChange={() => handleTogglePreference('notificacoes_email')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-title">
                  <i className="fas fa-desktop"></i>
                  Notificações no Sistema
                </div>
                <p className="setting-description">
                  Exiba notificações dentro do sistema quando estiver logado.
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences.notificacoes_sistema}
                  onChange={() => handleTogglePreference('notificacoes_sistema')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Seção de Privacidade */}
        <div className="settings-section">
          <div className="section-header">
            <h2>
              <i className="fas fa-lock"></i>
              Privacidade
            </h2>
            <p>Controle quem pode ver suas informações e como seus dados são usados</p>
          </div>

          <div className="settings-list">
            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-title">
                  <i className="fas fa-eye"></i>
                  Perfil Visível
                </div>
                <p className="setting-description">
                  Permite que outros usuários da mesma empresa vejam seu perfil e informações
                  básicas.
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences.perfil_visivel}
                  onChange={() => handleTogglePreference('perfil_visivel')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-title">
                  <i className="fas fa-share-alt"></i>
                  Compartilhamento de Dados
                </div>
                <p className="setting-description">
                  Permite que seus dados sejam usados para melhorar a experiência e gerar
                  relatórios agregados. Seus dados pessoais nunca serão compartilhados com
                  terceiros.
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences.compartilhar_dados}
                  onChange={() => handleTogglePreference('compartilhar_dados')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Seção de Segurança */}
        <div className="settings-section">
          <div className="section-header">
            <h2>
              <i className="fas fa-user-shield"></i>
              Segurança
            </h2>
            <p>Proteja sua conta e gerencie o acesso aos seus dados</p>
          </div>

          <div className="settings-list">
            <div className="setting-item clickable" onClick={() => navigate('/perfil')}>
              <div className="setting-info">
                <div className="setting-title">
                  <i className="fas fa-key"></i>
                  Alterar Senha
                </div>
                <p className="setting-description">
                  Altere sua senha regularmente para manter sua conta segura.
                </p>
              </div>
              <i className="fas fa-chevron-right setting-arrow"></i>
            </div>

            <div className="setting-item info-only">
              <div className="setting-info">
                <div className="setting-title">
                  <i className="fas fa-clock"></i>
                  Sessões Ativas
                </div>
                <p className="setting-description">
                  Você está logado neste dispositivo. Por segurança, faça logout ao terminar de
                  usar o sistema.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Informações sobre Dados */}
        <div className="settings-section info-section">
          <div className="section-header">
            <h2>
              <i className="fas fa-info-circle"></i>
              Sobre Seus Dados
            </h2>
          </div>

          <div className="info-box">
            <p>
              <strong>Proteção de Dados:</strong> Levamos a sério a privacidade dos seus dados.
              Todas as informações são armazenadas de forma segura e criptografada.
            </p>
            <p>
              <strong>Seus Direitos:</strong> Você tem o direito de acessar, corrigir ou excluir
              seus dados pessoais a qualquer momento.
            </p>
            <p>
              <strong>Dúvidas?</strong> Entre em contato com o administrador da sua empresa para
              mais informações sobre como seus dados são tratados.
            </p>
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSavePreferences}
            disabled={saving}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Salvando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Salvar Preferências
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettings;
